/**
 * Token Refresh Manager
 *
 * Apollo errorLink, REST axios 인터셉터 등 여러 레이어에서 공유하는 토큰 갱신 모듈.
 * 동시에 여러 요청이 401을 받더라도 /auth/refresh는 한 번만 호출되도록
 * isRefreshing 플래그 + pendingRequests 큐로 race condition을 방지한다.
 *
 * 앱별로 refresh 실패 시 동작을 커스터마이즈하려면 configureTokenRefreshManager()를
 * 앱 진입점에서 호출한다.
 */
import Cookies from 'js-cookie';
import jwtDecode, { JwtPayload } from 'jwt-decode';
import { saveAccessToken } from './auth';
import { LOGIN_AUTH_TOKEN_KEY } from '../constants/token';
import { isDomainAuthProject } from '../constants/projectTarget';

/** refresh 결과 타입 — 실패 시 원인을 구분하여 호출부에서 적절히 처리할 수 있다 */
export type RefreshResult =
  | { success: true; token: string }
  | { success: false; reason: 'auth' | 'transient' };

interface TokenRefreshManagerConfig {
  /** refresh 실패 시 호출되는 콜백 (예: 로그인 페이지 리다이렉트) */
  onFailure?: () => void;
}

/** 앱에서 주입한 설정 */
let config: TokenRefreshManagerConfig = {};

/**
 * 앱 진입점에서 호출하여 refresh 실패 시 동작을 설정한다.
 * 예: configureTokenRefreshManager({ onFailure: () => { window.location.href = '/login' } })
 */
export function configureTokenRefreshManager(options: TokenRefreshManagerConfig) {
  config = options;
}

/** refresh 진행 중 여부 — 동시 호출 방지용 플래그 */
let isRefreshing = false;

/**
 * 다른 탭이 이미 갱신한 Access Token이 아직 유효한지 확인한다.
 * Cross-tab lock 획득 후 호출하여 불필요한 /auth/refresh 요청을 방지한다.
 *
 * @param tokenBeforeLock lock 획득 전 저장한 토큰 — 쿠키의 토큰이 이 값과 다를 때만
 *   "다른 탭이 갱신함"으로 판단하여, authLink의 60초 선제 갱신 버퍼와 충돌하지 않는다.
 */
function getTokenIfFresh(tokenBeforeLock: string | undefined): string | null {
  const cookieToken = Cookies.get(LOGIN_AUTH_TOKEN_KEY);
  if (!cookieToken) return null;
  // 토큰이 변경되지 않았으면 다른 탭이 갱신한 것이 아님 — skip 불가
  if (cookieToken === tokenBeforeLock) return null;
  try {
    const decoded = jwtDecode<JwtPayload>(cookieToken);
    if (decoded.exp && decoded.exp - Date.now() / 1000 > 30) {
      return cookieToken;
    }
  } catch {
    // decode 실패 시 null 반환
  }
  return null;
}
/** refresh 진행 중 대기하는 요청들의 resolve 콜백 큐 */
let pendingRequests: Array<(result: RefreshResult) => void> = [];
/** 로그아웃(리다이렉트) 진행 중 여부 — handleRefreshFailure 무한 루프 방지 */
let isLoggingOut = false;

const getAuthServerUrl = () => {
  if (isDomainAuthProject()) {
    return '/mildang';
  }

  const graphQLUrl = process.env.NEXT_PUBLIC_GRAPHQL_URL;
  if (!graphQLUrl) {
    return '';
  }

  return new URL(graphQLUrl).origin;
};

/** 인증 에러 여부 판별 — 401/403은 refresh token 자체가 무효하므로 재시도 무의미 */
const isAuthError = (status: number) => status === 401 || status === 403;

/** 인증 에러 전용 — catch 블록에서 일시적 에러와 구분하기 위한 클래스 */
class AuthRefreshError extends Error {
  constructor(status: number) {
    super(`Refresh auth error: ${status}`);
    this.name = 'AuthRefreshError';
  }
}

/** 일시적 에러 (5xx 등) 시 재시도 설정 */
const MAX_REFRESH_RETRIES = 2;
const RETRY_DELAYS = [1000, 2000]; // ms

/** 대기 중인 모든 요청에 결과를 전달하고 큐를 비운다 */
const resolvePendingRequests = (result: RefreshResult) => {
  pendingRequests.forEach((callback) => callback(result));
  pendingRequests = [];
};

/**
 * Access Token을 갱신한다.
 * - 이미 refresh 진행 중이면 대기 후 결과를 받는다.
 * - 성공 시 새 Access Token을 js-cookie에 저장하고 반환한다.
 * - 인증 에러(401/403) 시 { success: false, reason: 'auth' } — 로그아웃 필요.
 * - 일시적 에러(5xx, 네트워크) 시 { success: false, reason: 'transient' } — 로그아웃 불필요.
 */
export async function refreshAccessToken(): Promise<RefreshResult> {
  // 이미 로그아웃 진행 중이면 refresh 시도하지 않음 (무한 루프 방지)
  if (isLoggingOut) return { success: false, reason: 'auth' };

  if (isRefreshing) {
    return new Promise((resolve) => {
      pendingRequests.push(resolve);
    });
  }

  isRefreshing = true;
  try {
    const result = await refreshWithCrossTabLock();
    // 대기 중이던 모든 요청에 결과 전달
    resolvePendingRequests(result);
    return result;
  } catch (error) {
    const reason = error instanceof AuthRefreshError ? 'auth' : ('transient' as const);
    const result: RefreshResult = { success: false, reason };
    resolvePendingRequests(result);
    return result;
  } finally {
    isRefreshing = false;
  }
}

/**
 * Cross-tab lock을 획득한 뒤 refresh를 수행한다.
 * Web Locks API를 지원하는 브라우저에서는 탭 간 상호 배제가 보장되며,
 * lock 획득 후 다른 탭이 이미 갱신한 토큰이 유효하면 네트워크 요청을 건너뛴다.
 *
 * 미지원 브라우저에서는 기존 동작(탭 내 isRefreshing dedup만)으로 동작한다.
 */
async function refreshWithCrossTabLock(): Promise<RefreshResult> {
  // Lock 획득 전 현재 토큰 저장 — 다른 탭의 갱신 감지용
  const tokenBeforeLock = Cookies.get(LOGIN_AUTH_TOKEN_KEY);

  if (typeof navigator !== 'undefined' && 'locks' in navigator) {
    return navigator.locks.request('md-token-refresh', async () => {
      // Lock 획득 후 freshness check — 대기 중 다른 탭이 이미 갱신했을 수 있음
      const freshToken = getTokenIfFresh(tokenBeforeLock);
      if (freshToken) {
        return { success: true, token: freshToken } as RefreshResult;
      }
      return doAttemptRefresh();
    });
  }
  // Web Locks 미지원 시 기존 동작 (탭 내 dedup만)
  return doAttemptRefresh();
}

/** 실제 refresh 호출 + 토큰 저장 래퍼 */
async function doAttemptRefresh(): Promise<RefreshResult> {
  const newAccessToken = await attemptRefresh();
  saveAccessToken(newAccessToken);
  return { success: true, token: newAccessToken };
}

/**
 * 실제 /auth/refresh 요청을 수행한다.
 * - 인증 에러(401/403): 즉시 throw (재시도 불필요 — refresh token 무효)
 * - 일시적 에러(5xx, 네트워크): 최대 MAX_REFRESH_RETRIES회 재시도 후 throw
 */
async function attemptRefresh(): Promise<string> {
  const authServerUrl = getAuthServerUrl();
  let lastError: unknown;

  for (let attempt = 0; attempt <= MAX_REFRESH_RETRIES; attempt++) {
    try {
      // credentials: 'include' → 브라우저가 mdRefreshToken httpOnly 쿠키를 자동으로 전송
      const response = await fetch(`${authServerUrl}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        return data.accessToken;
      }

      // 인증 에러(401/403) → refresh token 자체가 무효하므로 즉시 실패
      if (isAuthError(response.status)) {
        throw new AuthRefreshError(response.status);
      }

      // 기타 에러(5xx 등) → 재시도 가능
      lastError = new Error(`Refresh server error: ${response.status}`);
    } catch (error) {
      // 인증 에러로 throw된 경우 즉시 전파 (재시도하지 않음)
      if (error instanceof AuthRefreshError) {
        throw error;
      }
      // 네트워크 에러 등 → 재시도 가능
      lastError = error;
    }

    // 마지막 시도가 아니면 대기 후 재시도
    if (attempt < MAX_REFRESH_RETRIES) {
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS[attempt]));
    }
  }

  throw lastError;
}

/**
 * Refresh 실패 시 호출 — 서버 로그아웃 + Access Token 삭제 후 리다이렉트
 * 앱별 리다이렉트 경로는 configureTokenRefreshManager({ onFailure })로 설정한다.
 */
export function handleRefreshFailure() {
  // 이미 로그아웃 진행 중이면 중복 실행 방지 (무한 루프 방지)
  if (isLoggingOut) return;
  isLoggingOut = true;
  const authServerUrl = getAuthServerUrl();

  // 서버에 로그아웃 요청 — httpOnly mdRefreshToken 쿠키 삭제 (fire-and-forget)
  if (authServerUrl) {
    fetch(`${authServerUrl}/auth/logout`, {
      method: 'POST',
      // credentials: 'include' → 브라우저가 mdRefreshToken httpOnly 쿠키를 자동으로 전송
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${Cookies.get(LOGIN_AUTH_TOKEN_KEY) ?? ''}`,
      },
    }).catch(() => {});
  }

  Cookies.remove(LOGIN_AUTH_TOKEN_KEY);

  if (config.onFailure) {
    config.onFailure();
  } else {
    window.location.href = '/';
  }
}
